/**
 * @freealbum/ui — freeAlbum design system.
 *
 * Import the tokens once in the app's global stylesheet:
 *   @import 'tailwindcss';
 *   @import '@freealbum/ui/tokens.css';
 *
 * and wrap the tree once:
 *   <UIProvider dir="rtl">…</UIProvider>
 *
 * Every component uses CSS logical properties (ps/pe/ms/me/start/end) — there is
 * no physical left/right in this package, so the whole UI mirrors for Hebrew.
 */

export { cn, type ClassValue } from './cn';

export {
  useElementDirection,
  useControllableState,
  useId,
  useMounted,
  useMediaQuery,
  useIsMobile,
  useComposedRefs,
  type Direction,
} from './hooks';

export {
  UIProvider,
  useUILabels,
  DEFAULT_UI_LABELS,
  type UIProviderProps,
  type UILabels,
} from './UIProvider';

export {
  Button,
  BUTTON_BASE,
  BUTTON_SIZE,
  BUTTON_VARIANT,
  type ButtonProps,
  type ButtonSize,
  type ButtonVariant,
} from './Button';
export { IconButton, type IconButtonProps } from './IconButton';

export {
  Field,
  CONTROL_BASE,
  CONTROL_SIZE,
  CONTROL_TEXT,
  type FieldProps,
  type ControlSize,
} from './Field';
export { Input, type InputProps } from './Input';
export { Textarea, type TextareaProps } from './Textarea';
export { Select, type SelectProps, type SelectOption } from './Select';
export { Switch, type SwitchProps } from './Switch';
export { Slider, type SliderProps } from './Slider';
export {
  SegmentedControl,
  type SegmentedControlProps,
  type SegmentedOption,
} from './SegmentedControl';

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  type TabsProps,
  type TabsListProps,
  type TabsTriggerProps,
  type TabsContentProps,
} from './Tabs';

export {
  Sheet,
  SheetRoot,
  SheetTrigger,
  SheetClose,
  type SheetProps,
  type SheetSide,
  type SheetSize,
} from './Sheet';

export {
  Dialog,
  DialogRoot,
  DialogTrigger,
  DialogClose,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  type DialogProps,
  type DialogContentProps,
  type DialogSize,
} from './Dialog';

export {
  Popover,
  PopoverRoot,
  PopoverTrigger,
  PopoverAnchor,
  PopoverClose,
  PopoverContent,
  type PopoverProps,
  type PopoverContentProps,
} from './Popover';

export {
  DropdownMenu,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  type DropdownMenuItemProps,
} from './DropdownMenu';

export {
  Tooltip,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  type TooltipProps,
} from './Tooltip';

export {
  Toaster,
  useToast,
  toast,
  dismissToast,
  type ToastOptions,
  type ToastRecord,
  type ToastVariant,
  type ToasterProps,
  type ToasterPosition,
  type UseToastResult,
} from './Toast';

export { Badge, type BadgeProps, type BadgeVariant } from './Badge';
export { Spinner, type SpinnerProps } from './Spinner';
export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
  type CardProps,
} from './Card';
export { Separator, type SeparatorProps } from './Separator';
export { ColorSwatch, isLight, type ColorSwatchProps } from './ColorSwatch';
export {
  ColorPicker,
  ColorPickerPanel,
  DEFAULT_SWATCHES,
  normalizeHex,
  type ColorPickerProps,
  type ColorPickerPanelProps,
} from './ColorPicker';
export { Kbd, type KbdProps } from './Kbd';
export { ProgressBar, type ProgressBarProps } from './ProgressBar';
export { EmptyState, type EmptyStateProps } from './EmptyState';
